import type { IconName } from "@/components/ui/icon";

import type { EnrollmentOutcome, EnrollmentStatus } from "./types";

export interface StatusMeta {
  /** Human label shown in the status pill / filter chip. */
  label: string;
  /** Tailwind text-color class for the pill icon + label. */
  color: string;
  /** Centralicons name for the status icon. */
  icon: IconName;
  /** Whether the enrollment has reached a final state (no further steps run). */
  is_terminal: boolean;
}

export const ENROLLMENT_STATUS_META: Record<EnrollmentStatus, StatusMeta> = {
  scheduled: {
    label: "Scheduled",
    color: "text-slate-400",
    icon: "IconClock",
    is_terminal: false,
  },
  active: {
    label: "Active",
    color: "text-emerald-400",
    icon: "IconPlay",
    is_terminal: false,
  },
  paused: {
    label: "Paused",
    color: "text-amber-400",
    icon: "IconPause",
    is_terminal: false,
  },
  completed: {
    label: "Completed",
    color: "text-sky-400",
    icon: "IconCircleCheck",
    is_terminal: true,
  },
  cancelled: {
    label: "Cancelled",
    color: "text-muted-foreground",
    icon: "IconCircleMinus",
    is_terminal: true,
  },
  bounced: {
    label: "Bounced",
    color: "text-destructive",
    icon: "IconExclamationCircle",
    is_terminal: true,
  },
  error: {
    label: "Error",
    color: "text-destructive",
    icon: "IconExclamationTriangle",
    is_terminal: true,
  },
};

/**
 * Display order for the status filter — active (non-terminal) states first,
 * terminal states last — so recruiters scan live enrollments before history.
 */
export const ENROLLMENT_STATUS_ORDER: EnrollmentStatus[] = [
  "scheduled",
  "active",
  "paused",
  "completed",
  "cancelled",
  "bounced",
  "error",
];

export interface FacetMeta {
  label: string;
  /** Tailwind text-color class for the icon. */
  color: string;
  icon: IconName;
}

/**
 * The recruiter-facing reply outcome. Icons and sentiment match the outcome
 * pill on an enrollment row — gold interested, reddish not-interested, red
 * bounced, orange unknown, gray pending and opt-out — so the filter reads like
 * the column it filters.
 */
export const ENROLLMENT_OUTCOME_META: Record<EnrollmentOutcome, FacetMeta> = {
  interested: {
    label: "Interested",
    color: "text-amber-300",
    icon: "IconThumbsUp",
  },
  not_interested: {
    label: "Not Interested",
    color: "text-rose-400",
    icon: "IconThumbsDown",
  },
  bounced: {
    label: "Bounced",
    color: "text-destructive",
    icon: "IconRedirectArrow",
  },
  opted_out: {
    label: "Opted Out",
    color: "text-muted-foreground",
    icon: "IconMinusLarge",
  },
  unknown: {
    label: "Unknown",
    color: "text-orange-400",
    icon: "IconQuestionmarkCircle2",
  },
  pending: { label: "Pending", color: "text-slate-400", icon: "IconClock" },
};

export const ENROLLMENT_OUTCOME_ORDER: EnrollmentOutcome[] = [
  "interested",
  "not_interested",
  "bounced",
  "opted_out",
  "unknown",
  "pending",
];

export type EnrollmentEngagement = "opened" | "clicked" | "replied";

/** Engagement, coloured like the pills in the row's Engagement column. */
export const ENROLLMENT_ENGAGEMENT_META: Record<
  EnrollmentEngagement,
  FacetMeta
> = {
  opened: {
    label: "Opened",
    color: "text-sky-400",
    icon: "IconEmailNotification",
  },
  clicked: {
    label: "Clicked",
    color: "text-fuchsia-400",
    icon: "IconCursorClick",
  },
  replied: {
    label: "Replied",
    color: "text-emerald-400",
    icon: "IconEmail2Incoming",
  },
};
